import { NoiseSourceResponse_Scenario } from "services/sicalcApi/responses/noiseSourceResponse"
import styles from 'components/createCustomScenarioModal/createCustomScenarioModal.module.scss'
import { useFormik } from "formik"
import * as yup from 'yup'
import { ModalBase } from "components/common/modals/modalBase/modalBase"
import { ModalProps } from "services/modal/modalService"
import { PrimaryButton } from "components/common/buttons/primaryButton/primaryButton"
import { LightButton } from "components/common/buttons/lightButton/lightButton"

/**
 * The first step of creating a custom scenario. The filters and scaling are
 * chosen in the second step, see CustomScenarioBuilderModal.
 */
export interface CustomScenarioDetails {
  baseScenarioId: string
  name: string
  targetYear: number
  startWithNoTraffic: boolean
}

interface CreateCustomScenarioModalProps extends ModalProps {
  scenarios: NoiseSourceResponse_Scenario[] | undefined
}

interface FormDataModel {
  baseScenarioId: string
  customScenarioName: string
  targetYear: string | number
  startWithFullTraffic: 'yes' | 'no'
}

const formValidationSchema = yup.object().shape({
  baseScenarioId: yup.string()
    .required("Påkrevd"),
  customScenarioName: yup.string()
    .required("Påkrevd"),
  targetYear: yup.number()
    .typeError('Må være et tall')
    .min(0, "Må være positivt")
    .required("Påkrevd"),
})

export function CreateCustomScenarioModal(props: CreateCustomScenarioModalProps) {
  const handleSubmission = (values: FormDataModel) => {
    const details: CustomScenarioDetails = {
      baseScenarioId: values.baseScenarioId,
      name: values.customScenarioName,
      targetYear: values.targetYear as number,
      startWithNoTraffic: values.startWithFullTraffic === 'no',
    }

    props.modalContext.close(details)
  }

  const initialValues: FormDataModel = {
    baseScenarioId: '',
    customScenarioName: '',
    targetYear: '',
    startWithFullTraffic: 'yes',
  }

  const formik = useFormik({
    initialValues,
    validationSchema: formValidationSchema,
    onSubmit: handleSubmission,
  })

  const dismiss = () => {
    props.modalContext.dismiss()
  }

  return (
    <ModalBase
      onDismiss={dismiss}
      heading="Nytt scenario"
      actions={
        <>
          <LightButton onClick={dismiss}>
            Avbryt
          </LightButton>
          <PrimaryButton
            onClick={formik.handleSubmit}
            submit={true}
          >
            Neste
          </PrimaryButton>
        </>
      }
    >
      <div className={`${styles.body} column`}>
        <label className={'column'}>
          <span className={'form-label '}>Velg hvilket scenario som skal skaleres</span>
          <select
            name={"baseScenarioId"}
            value={formik.values.baseScenarioId}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className={`input ${formik.touched.baseScenarioId && formik.errors.baseScenarioId ? 'invalid' : ''}`}
          >
            <option disabled value={''}>Velg</option>
            {props.scenarios?.map(s => (
              <option value={s.id} key={s.id}>
                {s.targetYear} – {s.name}
              </option>
            ))}
          </select>
          {formik.touched.baseScenarioId && formik.errors.baseScenarioId && (
            <div className={'validation-error'}>
              {formik.errors.baseScenarioId}
            </div>
          )}
        </label>
        <label className={'column'}>
          <span className={'form-label '}>Navn</span>
          <input
            name={"customScenarioName"}
            value={formik.values.customScenarioName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className={`input ${formik.touched.customScenarioName && formik.errors.customScenarioName ? 'invalid' : ''}`}
            type="text"
          />
          {formik.touched.customScenarioName && formik.errors.customScenarioName && (
            <div className={'validation-error'}>
              {formik.errors.customScenarioName}
            </div>
          )}
        </label>
        <label className={'column'}>
          <span className={'form-label '}>Beregningsår</span>
          <input
            name={"targetYear"}
            value={formik.values.targetYear}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className={`input ${formik.touched.targetYear && formik.errors.targetYear ? 'invalid' : ''}`}
            type="text"
          />
          {formik.touched.targetYear && formik.errors.targetYear && (
            <div className={'validation-error'}>
              {formik.errors.targetYear}
            </div>
          )}
        </label>

        <div className={'column'}>
          <span className={'form-label'}>Starte med fullt datagrunnlag?</span>
          <div className={`${styles['radio-group']} row`}>
            <label className={`${styles.radio} row align-center`}>
              <input
                type="radio"
                name={"startWithFullTraffic"}
                value={'yes'}
                checked={formik.values.startWithFullTraffic === 'yes'}
                onChange={formik.handleChange}
              />
              <span>Ja – all trafikk er med, filtrene justerer den</span>
            </label>
            <label className={`${styles.radio} row align-center`}>
              <input
                type="radio"
                name={"startWithFullTraffic"}
                value={'no'}
                checked={formik.values.startWithFullTraffic === 'no'}
                onChange={formik.handleChange}
              />
              <span>Nei – start fra null, filtrene legger til trafikk</span>
            </label>
          </div>
        </div>
      </div>
    </ModalBase>
  )
}
